import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader, useToaster, Message, IconButton } from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';

import BasicPanel from '../../components/panels/basicPanel/basicPanel';
import BasicDropdown from '../../components/inputs/basicDropdown/basicDropdown';
import BasicWrapper from '../../components/basicWrapper/basicWrapper';
import ConfirmModal from '../../components/confirmModal/confirmModal';
import { useLanguage } from '../../context/localeContext';
import Btn from '../../components/Btn/Btn';
import { useMessenger } from '../../context/messengerContext';
import { getContractsFullByID } from '../../api/auth';
import { getEmployees, deleteEmployee } from '../../api/employee';

import './employeesList.css';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';
import { getProfile } from '../../api/user';

const EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY = 'employees:selectedContract';

export default function EmployeesList() {
    const { t } = useTranslation('basic');
    const { userId, platform, showBackButton, hideBackButton } = useMessenger();
    const navigate = useNavigate();
    const toaster = useToaster();

    const [contractOptions, setContractOptions] = useState([]);
    const [selectedContract, setSelectedContract] = useState(() => {
        if (typeof window === 'undefined') return '';
        return (
            sessionStorage.getItem(EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY) ??
            ''
        );
    });
    const [employees, setEmployees] = useState([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [contractsLoading, setContractsLoading] = useState(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        showBackButton(() => navigate(-1));
        return () => hideBackButton();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setContractsLoading(true);
            try {
                const profile = await getProfile({ platform, userId });
                const contracts = await getContractsFullByID({
                    platform,
                    userId,
                });
                if (cancelled) return;
                const list = Array.isArray(contracts) ? contracts : [];

                const adminContractNumbers = new Set(
                    (Array.isArray(profile?.employees) ? profile.employees : [])
                        .filter((e) => e?.is_admin === true)
                        .map((e) => e?.client?.contract_number)
                        .filter(Boolean),
                );

                const filteredList =
                    adminContractNumbers.size > 0
                        ? list.filter((c) =>
                              adminContractNumbers.has(c?.contract_number),
                          )
                        : [];

                const toOption = (c) => ({
                    label: c.contract_number ?? '',
                    value: c.contract_number ?? '',
                });

                console.log('profile?.employees?.length', !!profile?.employees?.length);
                if (!!profile?.employees?.length) {
                    setContractOptions(filteredList.map(toOption));
                    return;
                }

                setContractOptions(list.map(toOption));
            } catch (err) {
                if (cancelled) return;
                toaster.push(
                    <Message type='error' showIcon>
                        {err?.message || t('employees_contracts_error')}
                    </Message>,
                    { placement: 'topCenter', duration: 3000 },
                );
            } finally {
                if (!cancelled) setContractsLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [platform, userId, toaster, t]);

    useEffect(() => {
        if (!selectedContract) return;
        if (!contractOptions.length) return;
        const exists = contractOptions.some(
            (o) => o.value === selectedContract,
        );
        if (!exists) {
            setSelectedContract('');
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem(
                    EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY,
                );
            }
        }
    }, [contractOptions, selectedContract]);

    useEffect(() => {
        if (!selectedContract) {
            setEmployees([]);
            return;
        }
        let cancelled = false;
        setEmployeesLoading(true);
        setEmployees([]);
        getEmployees({ contract_number: selectedContract })
            .then((res) => {
                if (cancelled) return;
                setEmployees(Array.isArray(res) ? res : []);
            })
            .catch((err) => {
                if (cancelled) return;
                toaster.push(
                    <Message type='error' showIcon>
                        {err?.message || t('employees_load_error')}
                    </Message>,
                    { placement: 'topCenter', duration: 3000 },
                );
                setEmployees([]);
            })
            .finally(() => {
                if (!cancelled) setEmployeesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedContract, toaster, t]);

    const formatEmployeeName = (emp) => {
        const parts = [
            emp?.last_name,
            emp?.first_name,
            emp?.middle_name != null && emp?.middle_name !== ''
                ? emp?.middle_name
                : null,
        ].filter(Boolean);
        return parts.join(' ');
    };

    const openDeleteModal = (emp) => {
        setEmployeeToDelete(emp);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setEmployeeToDelete(null);
        setDeleteError(null);
    };

    const handleConfirmDelete = async () => {
        if (employeeToDelete == null || !selectedContract) return;
        const empTgId = employeeToDelete.tg_id;
        const empMaxId = employeeToDelete.max_id;
        if (
            (empTgId == null || empTgId === '') &&
            (empMaxId == null || empMaxId === '')
        ) {
            setDeleteError(t('employees_delete_no_tg_id'));
            return;
        }
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await deleteEmployee({
                contract_number: selectedContract,
                tg_id: empTgId,
                max_id: empMaxId,
            });
            setEmployees((prev) =>
                prev.filter((e) => {
                    if (empTgId) return e.tg_id !== empTgId;
                    return e.max_id !== empMaxId;
                }),
            );
            closeDeleteModal();
            toaster.push(
                <Message type='success' showIcon>
                    {t('employees_delete_success')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } catch (err) {
            const msg = err?.message || t('employees_delete_error');
            setDeleteError(msg);
            toaster.push(
                <Message type='error' showIcon>
                    {msg}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const isIOS26Version = isIOSVersionHigherThan26Version();

    const isNoEmployees =
        !employeesLoading && selectedContract && employees.length === 0;

    const handleContractChange = (value) => {
        setSelectedContract(value);
        if (typeof window !== 'undefined') {
            if (value) {
                sessionStorage.setItem(
                    EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY,
                    value,
                );
            } else {
                sessionStorage.removeItem(
                    EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY,
                );
            }
        }
    };

    return (
        <div
            className={`stepsHeaderWrapper gradient_bg_header ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
        >
            <BasicWrapper>
                <div className='employeesList_header'>
                    <div className='stepHeaderHeader heading-h4'>
                        {t('employees_list_title')}
                        <img
                            id='stepsHeaderImage'
                            src='https://s3.timeweb.com/8a6e091b-publick/template_header.png'
                            alt=''
                        />
                    </div>
                </div>

                <div className='employeesList_actions'>
                    <Btn
                        text={t('employees_invite_button')}
                        fluid
                        onClick={() => navigate('/employee_invite')}
                    />
                </div>

                <div className='employeesList_selectWrap'>
                    <BasicDropdown
                        label={t('contract_number_label')}
                        placeholder={t('select_contract_number')}
                        data={contractOptions}
                        value={selectedContract}
                        onChange={handleContractChange}
                        disabled={contractsLoading}
                    />
                </div>

                {employeesLoading && (
                    <div className='employeesList_loader'>
                        <Loader size='lg' />
                    </div>
                )}

                {isNoEmployees && (
                    <div className='employeesList_empty'>
                        <p className='employeesList_emptyText'>
                            {t('employees_empty')}
                        </p>
                    </div>
                )}

                {!employeesLoading && employees.length > 0 && (
                    <ul className='employeesList_list'>
                        {employees.map((emp) => (
                            <li
                                key={
                                    emp.user_mongo_id ??
                                    emp.tg_id ??
                                    emp.last_name + emp.first_name
                                }
                                className='employeesList_card'
                            >
                                <BasicPanel>
                                    <div className='employeesList_cardRow'>
                                        <span className='paragraph-14R'>
                                            {formatEmployeeName(emp)}
                                        </span>
                                        <div
                                            role='button'
                                            tabIndex={0}
                                            onClick={() => openDeleteModal(emp)}
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === 'Enter' ||
                                                    e.key === ' '
                                                ) {
                                                    e.preventDefault();
                                                    openDeleteModal(emp);
                                                }
                                            }}
                                            aria-label={t('employees_delete')}
                                        >
                                            <IconButton
                                                icon={<TrashIcon />}
                                                appearance='subtle'
                                                color='red'
                                                size='md'
                                            />
                                        </div>
                                    </div>
                                </BasicPanel>
                            </li>
                        ))}
                    </ul>
                )}

                <ConfirmModal
                    open={deleteModalOpen}
                    onClose={closeDeleteModal}
                    onConfirm={handleConfirmDelete}
                    titleKey='employees_delete_confirm_title'
                    bodyKey='employees_delete_confirm_body'
                    bodyInterpolation={{
                        name:
                            employeeToDelete != null
                                ? formatEmployeeName(employeeToDelete)
                                : '',
                    }}
                    confirmTextKey='employees_delete_confirm_btn'
                    error={deleteError}
                    isLoading={isDeleting}
                />
            </BasicWrapper>
        </div>
    );
}
